import { Typography, Label, NativeSelect, Button } from "@altimateai/lego";
import { cn } from "@/lib/utils";
import DayOfMonthSelect from "@/components/dayOfMonthSelect";
import type { ScheduleEditorProps } from "../types";
import { DAYS_OF_WEEK, TIME_OPTIONS } from "../constants";

const FREQUENCY_OPTIONS = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];

const TIME_SELECT_OPTIONS = TIME_OPTIONS.map(time => ({ label: time, value: time }));

const ScheduleEditor = ({
  scheduleData,
  validationError,
  onFrequencyChange,
  onTimeChange,
  onDayOfWeekToggle,
  onDayOfMonthChange,
}: ScheduleEditorProps) => {
  return (
    <div className="al-space-y-4">
      <div className="al-grid al-grid-cols-2 al-gap-3">
        <div>
          <Label htmlFor="frequency" className="al-text-sm al-font-medium">
            Frequency
          </Label>
          <NativeSelect
            id="frequency"
            className="al-mt-1 al-w-full"
            options={FREQUENCY_OPTIONS}
            value={scheduleData.frequency}
            onChange={val => onFrequencyChange(val as "daily" | "weekly" | "monthly")}
          />
        </div>
        <div>
          <Label htmlFor="check-at" className="al-text-sm al-font-medium">
            Time
          </Label>
          <NativeSelect
            id="check-at"
            className="al-mt-1 al-w-full"
            options={TIME_SELECT_OPTIONS}
            value={scheduleData.time}
            onChange={onTimeChange}
          />
        </div>
      </div>

      {/* Weekly: Day of week selector (single selection) */}
      {scheduleData.frequency === "weekly" && (
        <div>
          <Label className="al-text-sm al-font-medium al-mb-2 al-block">
            Select a day of the week
          </Label>
          <div className="al-grid al-grid-cols-7 al-gap-2">
            {DAYS_OF_WEEK.map(day => {
              const isSelected = scheduleData.daysOfWeek?.includes(day) || false;
              return (
                <Button
                  key={day}
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onDayOfWeekToggle(day)}
                  className={cn(
                    "al-px-3 al-py-2 al-rounded-lg al-text-xs al-font-medium al-transition-all",
                    isSelected
                      ? "al-bg-blue-600 al-text-white hover:al-bg-blue-700"
                      : "al-bg-gray-100 al-text-gray-700 hover:al-bg-gray-200"
                  )}
                >
                  {day}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Monthly: Day of month selector */}
      {scheduleData.frequency === "monthly" && (
        <div>
          <Label htmlFor="day-of-month" className="al-text-sm al-font-medium">
            Day of the month
          </Label>
          <DayOfMonthSelect
            id="day-of-month"
            className="al-mt-1 al-w-full"
            value={String(scheduleData.dayOfMonth || 1)}
            onValueChange={val => onDayOfMonthChange(parseInt(val))}
          />
        </div>
      )}

      {validationError && (
        <Typography variant="body" size="xs" className="al-text-red-600 al-text-xs">
          {validationError}
        </Typography>
      )}
    </div>
  );
};

export default ScheduleEditor;
